import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Menu, LogOut, Settings, Box, LayoutDashboard, Receipt, Users, FileText, History, UserCog, DollarSign, Image, Building2, AlertTriangle, Wand2, Upload, Tag, Package, ShoppingBag, Mail, Briefcase, Smartphone, RotateCcw, Merge, Ticket } from "lucide-react";

const Badge = ({ n, color = "bg-red-600" }) => n > 0 ? (
  <span className={`ml-auto min-w-[20px] h-[20px] px-1.5 rounded-full ${color} text-white text-[10px] font-bold flex items-center justify-center`}>
    {n > 99 ? "99+" : n}
  </span>
) : null;

export const MobileNav = ({ user, boxes, selectedBox, setSelectedBox, isModuleEnabled, sinpeUnsentCount, pendingOrdersCount, expenseAlertCount, onLogout, onSettings }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isMaster = user.role === "master";
  const isCajero = user.role === "cajero";
  const canSinpe = isModuleEnabled("sinpe") && (isMaster || (isCajero && selectedBox?.sinpe_email));

  const main = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  ];
  const menu = [
    { to: "/transactions", label: "Movimientos", icon: Receipt, id: "transactions" },
    { to: "/customers", label: "Clientes", icon: Users, id: "customers" },
    { to: "/debts", label: "Cuentas", icon: FileText, id: "debts" },
    isModuleEnabled("inventory") && { to: "/inventory", label: "Inventario", icon: Package, id: "inventory" },
    (user.is_super_master || selectedBox?.lottery_accessible) && { to: "/apuestas", label: "Apuestas y Sorteos", icon: Ticket, id: "apuestas" },
    { to: "/orders", label: "Pedidos en línea", icon: Package, id: "orders", badge: pendingOrdersCount, badgeColor: "bg-emerald-600" },
    { to: "/suppliers", label: "Proveedores y Facturas", icon: Package, id: "suppliers" },
    isModuleEnabled("layaways") && { to: "/layaways", label: "Apartados", icon: ShoppingBag, id: "layaways" },
    canSinpe && { to: "/sinpe-verify", label: "SINPE", icon: Mail, id: "sinpe-verify", badge: sinpeUnsentCount },
    isCajero && isModuleEnabled("sublimation") && { to: "/sublimacion", label: "Sublimación", icon: Image, id: "sublimacion" },
    isCajero && isModuleEnabled("arts") && { to: "/arts", label: "Artes IA", icon: Wand2, id: "arts" },
    { to: "/reports", label: "Reportes", icon: History, id: "reports" },
    { to: "/sinpe-log", label: "Registro SINPE", icon: Smartphone, id: "sinpe-log" },
    { to: "/credit-notes-report", label: "Notas de Crédito", icon: RotateCcw, id: "credit-notes" },
    { to: "/customers-dedupe", label: "Deduplicar Clientes", icon: Merge, id: "dedupe" },
    { to: "/import", label: "Importar", icon: Upload, id: "import" },
    isModuleEnabled("labels") && { to: "/labels", label: "Etiquetas", icon: Tag, id: "labels" },
    isMaster && user.is_super_master && { to: "/lottery-access", label: "Acceso a Apuestas", icon: Ticket, id: "lottery-access" },
    isMaster && { to: "/hr", label: "Recursos Humanos", icon: Briefcase, id: "hr" },
    isMaster && { to: "/consolidated", label: "Consolidado", icon: FileText, id: "consolidated" },
    isMaster && { to: "/emergency", label: "Emergencia", icon: AlertTriangle, id: "emergency", danger: true },
  ].filter(Boolean);
  const bottom = [
    isMaster && { to: "/boxes", label: "Cajas", icon: Box, id: "boxes" },
    isMaster && { to: "/banks", label: "Bancos", icon: Building2, id: "banks" },
    isMaster && user.is_super_master && { to: "/users", label: "Usuarios", icon: UserCog, id: "users" },
    { to: "/expenses", label: "Gastos", icon: DollarSign, id: "expenses", badge: expenseAlertCount },
  ].filter(Boolean);

  const Item = ({ it, indent }) => {
    const active = location.pathname === it.to;
    const Icon = it.icon;
    return (
      <Link
        to={it.to}
        onClick={() => setOpen(false)}
        data-testid={`mobile-nav-${it.id}-link`}
        className={`flex items-center gap-3 rounded-md py-2.5 ${indent ? "pl-6 pr-3 text-sm" : "px-3 text-base font-medium"} transition-colors ${active ? "bg-slate-900 text-white" : it.danger ? "text-red-600 hover:bg-red-50" : "text-slate-700 hover:bg-slate-100"}`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span>{it.label}</span>
        <Badge n={it.badge} color={it.badgeColor} />
      </Link>
    );
  };

  const alertTotal = (sinpeUnsentCount || 0) + (pendingOrdersCount || 0) + (expenseAlertCount || 0);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="md:hidden relative" data-testid="mobile-menu-button" aria-label="Abrir menú">
          <Menu className="w-5 h-5" />
          {alertTotal > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-600 border border-white" />}
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[85vw] max-w-xs p-0 flex flex-col" data-testid="mobile-menu-sheet">
        <SheetHeader className="px-4 pt-4 pb-3 border-b border-slate-200 text-left">
          <SheetTitle style={{ fontFamily: "Space Grotesk, sans-serif" }}>PaliStore</SheetTitle>
          <div className="flex items-center justify-between pt-1">
            <div>
              <p className="text-sm font-medium text-slate-800" data-testid="mobile-menu-username">{user.username}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full ${isMaster ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
                {isMaster ? "Master" : "Cajero"}
              </span>
            </div>
          </div>
          {boxes.length > 0 && (
            <Select
              value={selectedBox?.id}
              onValueChange={(v) => setSelectedBox(boxes.find(b => b.id === v))}
              disabled={isCajero && !!user.assigned_box_id}
            >
              <SelectTrigger className="w-full mt-2" data-testid="mobile-box-selector-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {boxes.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </SheetHeader>
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
          {main.map(it => <Item key={it.id} it={it} />)}
          <p className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Menú</p>
          {menu.map(it => <Item key={it.id} it={it} indent />)}
          <div className="my-2 border-t border-slate-200" />
          {bottom.map(it => <Item key={it.id} it={it} />)}
        </nav>
        <div className="border-t border-slate-200 p-3 grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => { setOpen(false); onSettings(); }} data-testid="mobile-settings-button" className="gap-2">
            <Settings className="w-4 h-4" /> Ajustes
          </Button>
          <Button variant="outline" onClick={onLogout} data-testid="mobile-logout-button" className="gap-2 text-red-600 hover:bg-red-50">
            <LogOut className="w-4 h-4" /> Salir
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
